import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { ProvinceTaskAction, ProvinceTaskGetter } from '@/store/provinceTask/provinceTask.module';
import { ProvinceTaskSchema } from '@/store/provinceTask/provinceTask.validator';
import { ProvinceTask } from '@/store/provinceTask/provinceTask.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Datepicker from '@vuepic/vue-datepicker';

export default defineComponent({
  name: 'ProvinceTaskFormComponent',
  components: {
    BaseModalComponent,
    Datepicker,
  },
  props: {
    provinceTaskTypeId: {
      type: Number,
      required: true,
    },
    provinceId: {
      type: Number,
    },
    provinceTaskId: {
      type: Number,
    },
  },
  setup: (props) => {
    const swal = useSweetAlert();
    const store = useStore();
    const isEditMode = computed(() => !!props.provinceTaskId);
    const title = computed(() => isEditMode.value ? 'แก้ไขรายงาน' : 'เพิ่มรายงาน');
    const provinceTask = computed<ProvinceTask | undefined>(() => store.getters[ProvinceTaskGetter.BY_ID](props.provinceTaskId));
    const provinceTaskForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name?: string;
      description?: string;
      provinceTaskDate?: Date | null;
      provinceTaskTypeId: number;
      provinceId: number;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      description: '',
      provinceTaskDate: null,
      provinceTaskTypeId: props.provinceTaskTypeId,
      provinceId: props.provinceId as number,
    });
    const validator = computed(() => ProvinceTaskSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      description: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'description')),
      provinceTaskDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'provinceTaskDate')),
    });
    const isValid = computed(() => !errors.name && !errors.description && !errors.provinceTaskDate);
    const isLoading = computed<boolean>(() => store.getters[ProvinceTaskGetter.STATUS] === 'init' || store.getters[ProvinceTaskGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.provinceTaskTypeId = props.provinceTaskTypeId;
        formData.provinceId = props.provinceId as number;
        if (isEditMode.value) {
          formData.id = provinceTask.value?.id;
          formData.name = provinceTask.value?.name;
          formData.description = provinceTask.value?.description;
          formData.provinceTaskDate = provinceTask.value?.provinceTaskDate;
        }
        resolve();
      })
      .then(() => provinceTaskForm.value?.show());
    };

    // hide modal
    const hide = () => {
      provinceTaskForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.description = '';
        formData.provinceTaskDate = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(ProvinceTaskAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(ProvinceTaskAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(ProvinceTaskAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      provinceTaskForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  },
});
